import * as React from "react";
import { useState } from "react";
import {
  Button,
  makeStyles,
  Title2,
  Body1,
  Divider,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import { loginWithSSO } from '../../services/auth';

const logo = require("../../../public/assets/icon-128.png");

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "60px 20px",
    gap: "8px",
    minHeight: "100vh",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  logoContainer: {
    height: "120px",
    width: "120px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logoImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain"
  },
  textCenter: {
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  microsoftButton: {
    width: "100%",
    maxWidth: "280px",
    height: "44px",
    fontWeight: "600",
    backgroundColor: "#004d40", // Custom green as seen in screenshot
    color: "white",
    ":hover": {
      backgroundColor: "#00695c",
      cursor: "pointer",
    },
  },
  footer: {
    marginTop: "48px",
    textAlign: "center",
    paddingBottom: "20px"
  }
});

const Auth: React.FC<{ initialError?: string | null }> = ({ initialError }) => {
  const authStyles = useStyles();
  const [error, setError] = useState<string | null>(initialError ?? null);
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState("Signing in...");

  const handleMicrosoftLogin = async () => {
    setError(null);
    setLoading(true);
    console.log('[Auth] v3 — handleMicrosoftLogin called. SSO + dialog fallback active.');
    try {
      const result = await loginWithSSO();

      // onAuthStateChanged in App.tsx fires → Auth unmounts automatically.
      // For personal MSA accounts graphToken will be null here — that's expected.
      // OneDrive can be connected after sign-in via the Enable OneDrive button.
    } catch (e: any) {
      setError(e.message || "Sign-in failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className={authStyles.container}>
      <div className={authStyles.logoContainer}>
        <img src={logo} className={authStyles.logoImage} alt="MarginPro Logo" />
      </div>

      <div className={authStyles.textCenter}>
        <Title2 block style={{ textAlign: "center" }}>
          Welcome to <span style={{ whiteSpace: "nowrap" }}>Margin Pro</span>
        </Title2>
        <Body1 block style={{ marginTop: "4px", color: tokens.colorNeutralForeground2, textAlign: "center" }}>
          Connect your account to sync your notes across devices and collaborate in real-time.
        </Body1>
      </div>

      <Divider style={{ width: "80%", marginTop: "8px", marginBottom: "4px" }} />

      {error && (
        <Body1 block style={{ color: tokens.colorPaletteRedForeground1, textAlign: 'center', marginBottom: '10px' }}>
          Error: {error}
        </Body1>
      )}

      {loading ? (
        <Spinner label={statusText} />
      ) : (
        <Button
          appearance="primary"
          size="large"
          className={authStyles.microsoftButton}
          onClick={handleMicrosoftLogin}
        >
          Sign in with Microsoft
        </Button>
      )}

      <div className={authStyles.footer}>
        <Body1 style={{ color: tokens.colorNeutralForeground4, fontSize: '11px' }}>
          By signing in, you agree to our Terms and Privacy Policy.
        </Body1>
      </div>
    </div>
  );
};

export default Auth;
